/** OPTION G — Executive Questions.
 *  Board-room typography that rotates a sequence of pointed operational questions
 *  the platform actually answers.  The sign-in form sits underneath in a
 *  restrained, typography-led composition.  All motion is CSS-only.
 */
import { ArrowRight, Loader2, ChevronLeft, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { useLoginAuth } from "./useLoginAuth";

const QUESTIONS = [
  "Where is every laptop, server and monitor your company owns?",
  "Which assets are up for refresh in ninety days?",
  "How many devices were retired responsibly last quarter?",
  "What is the true cost of your Bengaluru estate this year?",
  "Which employees are still holding company equipment?",
];

export default function OptionG() {
  const a = useLoginAuth();
  return (
    <div className="min-h-screen bg-[color:var(--st-bg)] text-[color:var(--st-text)] flex flex-col" data-testid="login-option-g">
      <header className="border-b border-[color:var(--st-border)]">
        <div className="max-w-6xl mx-auto flex items-center h-14 px-6 md:px-8">
          <div className="text-[13px] font-semibold tracking-[0.14em]">SUNTRONIX ONE</div>
          <span className="mx-3 text-[color:var(--st-text-hint)]">·</span>
          <span className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">The platform that answers</span>
          <div className="ml-auto flex items-center gap-4 text-[11px] text-[color:var(--st-text-muted)]">
            <a href="/help/status" className="hover:text-[color:var(--st-text)] inline-flex items-center gap-1">
              <Activity className="h-3 w-3" strokeWidth={1.5} /> System status
            </a>
            <a href="/help" className="hover:text-[color:var(--st-text)]">Help</a>
          </div>
        </div>
      </header>

      <main className="flex-1 grid place-items-center px-6 md:px-8 py-14">
        <div className="w-full max-w-[860px]">
          {/* Rotating question typography */}
          <div className="text-center">
            <div className="text-[11px] uppercase tracking-[0.24em] text-[color:var(--st-text-muted)]">
              Every day, leadership asks
            </div>

            <div
              className="st-questions relative mt-8 mx-auto"
              style={{ "--n": QUESTIONS.length }}
              aria-live="polite"
              aria-atomic="true"
            >
              {QUESTIONS.map((q, i) => (
                <p
                  key={q}
                  className="st-question font-heading text-[30px] md:text-[42px] leading-[1.08] tracking-[-0.02em] font-medium text-[color:var(--st-text)]"
                  style={{ "--i": i }}
                >
                  {q}
                </p>
              ))}
              {/* Visually reserve height; screen readers get the invisible copy */}
              <p className="st-questions-ghost font-heading text-[30px] md:text-[42px] leading-[1.08] tracking-[-0.02em] font-medium" aria-hidden="true">
                {QUESTIONS.reduce((a, b) => (b.length > a.length ? b : a))}
              </p>
              <span className="sr-only">
                {QUESTIONS.join(". ")}
              </span>
            </div>

            <div className="mt-4 text-[13px] text-[color:var(--st-text-muted)] max-w-[46ch] mx-auto">
              SUNTRONIX ONE holds the answer — for every technology asset, every stage and every lifecycle.
            </div>

            {/* Dot pagination */}
            <div className="mt-6 flex items-center justify-center gap-1.5" aria-hidden="true">
              {QUESTIONS.map((_, i) => (
                <span key={i} className="st-question-dot" style={{ "--i": i }} />
              ))}
            </div>
          </div>

          {/* Divider */}
          <div className="mt-14 mx-auto h-px w-20 bg-[color:var(--st-border-strong)]" />

          {/* Auth form — typography-led, no card */}
          <div className="mt-14 mx-auto max-w-[380px]">
            <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)] text-center">
              Sign in to the answer
            </div>
            {a.step === "email" ? (
              <form onSubmit={a.continueEmail} className="mt-6 space-y-6">
                <UnderlineField label="Work email" type="email" value={a.email} onChange={a.setEmail}
                  required autoFocus autoComplete="email" placeholder="name@company.com" testId="g-email" />
                {a.error && <ErrorMsg msg={a.error} />}
                <button type="submit" disabled={a.loading || !a.email}
                  className="mx-auto flex items-center gap-2 text-[12px] uppercase tracking-[0.24em] text-[color:var(--st-text)] hover:text-[color:var(--st-accent)] disabled:opacity-50 py-2 transition-colors"
                  data-testid="g-continue">
                  {a.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Continue <ArrowRight className="h-4 w-4" strokeWidth={1.6} /></>)}
                </button>
              </form>
            ) : (
              <form onSubmit={a.submitPassword} className="mt-6 space-y-6">
                <button type="button" onClick={a.backToEmail}
                  className="mx-auto flex items-center gap-1 text-[11px] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]">
                  <ChevronLeft className="h-3 w-3" /> {a.email}
                </button>
                <UnderlineField label="Password" type="password" value={a.password} onChange={a.setPassword}
                  required autoFocus autoComplete="current-password" testId="g-password" />
                {a.error && <ErrorMsg msg={a.error} />}
                <div className="flex items-center justify-between">
                  <Link to="/forgot-password" className="text-[11px] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]">
                    Forgot password?
                  </Link>
                  <button type="submit" disabled={a.loading}
                    className="flex items-center gap-2 text-[12px] uppercase tracking-[0.24em] text-[color:var(--st-text)] hover:text-[color:var(--st-accent)] disabled:opacity-50 py-2 transition-colors">
                    {a.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Sign in <ArrowRight className="h-4 w-4" strokeWidth={1.6} /></>)}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </main>

      <footer className="border-t border-[color:var(--st-border)] py-5">
        <div className="max-w-6xl mx-auto px-6 md:px-8 flex justify-between text-[11px] text-[color:var(--st-text-subtle)]">
          <span>© 2026 Suntronix. All rights reserved.</span>
          <div className="flex gap-6">
            <a href="/help/trust" className="hover:text-[color:var(--st-text)]">Security &amp; Privacy</a>
            <a href="/help/accessibility" className="hover:text-[color:var(--st-text)]">Accessibility</a>
            <a href="/help/status" className="hover:text-[color:var(--st-text)]">System status</a>
            <a href="/help" className="hover:text-[color:var(--st-text)]">Help</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function UnderlineField({ label, value, onChange, testId, ...p }) {
  return (
    <label className="block text-center">
      <span className="sr-only">{label}</span>
      <input {...p} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="w-full h-12 px-0 border-0 border-b border-[color:var(--st-border-strong)] bg-transparent text-[17px] text-center focus:outline-none focus:border-[color:var(--st-text)] placeholder:text-[color:var(--st-text-hint)]" />
    </label>
  );
}
function ErrorMsg({ msg }) {
  return (
    <div role="alert" data-testid="g-error" className="text-[12px] text-[color:var(--st-critical)] text-center">
      {msg}
    </div>
  );
}
